import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { Drawer } from 'antd';
import CloseIcon from '@mui/icons-material/Close';
import CircularProgress from '@mui/material/CircularProgress';
import { DateTimePickerInput } from 'v3/Components/Form/DateTimePickers/DateTimePicker';
import TextInput from 'v3/Components/Form/TextInput';
import FileUploadBox, { UploadedFile } from 'v3/Components/Form/FileUploadBox';
import { PrimaryButton, TertiaryButton } from 'v3/Components/Shared/Buttons';
import { useGlobalState, useToasts } from 'Context';
import { buildEndpoint, useDataPoster, useHasPermissionsV3 } from 'Utilities';
import { ENDPOINTS, METHODS } from 'Models/Templates/Routes/Routes';
import { PermissionCodesV3 } from 'Models/Authorization/permissionCodesV3';
import { isPastRawDate } from 'Utilities/DateTimeUtc';
import { useSetFieldValueTouched } from 'v3/Components/Form/useSetFieldValueTouched';
import { BoardCertification } from './ReviewerProfile.Overview.LicenseInformation';

type FormValues = {
  expirationDate: string;
  certificationNumber: string;
  title: string;
  yearActivePracticeStarted: string;
};

const INITIAL_VALUES: FormValues = {
  expirationDate: '',
  certificationNumber: '',
  title: '',
  yearActivePracticeStarted: '',
};

type Props = {
  mode: 'add' | 'update';
  reviewerId: string;
  certification?: BoardCertification;
  open: boolean;
  onClose: () => void;
  onSuccess: (certification: BoardCertification) => void;
};

export const BoardCertificationDrawer: React.FC<Props> = ({
  mode,
  reviewerId,
  certification,
  open,
  onClose,
  onSuccess,
}) => {
  const toast = useToasts();
  const { baseUrl } = useGlobalState();
  const isUpdate = mode === 'update';
  const hasManagePrivileged = useHasPermissionsV3()(PermissionCodesV3.Reviewer.Certification.ManagePrivileged);

  const [saving, setSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<UploadedFile | null>(null);

  const formik = useFormik<FormValues>({
    initialValues: INITIAL_VALUES,
    validate: (values) => {
      const errors: Partial<Record<keyof FormValues, string>> = {};
      // CertificationNumber/Title are disabled and omitted from the payload on update by a
      // non-privileged user (see handleSave), so they aren't required in that case either.
      const privilegedFieldsRequired = !isUpdate || hasManagePrivileged;

      if (!values.expirationDate) {
        errors.expirationDate = 'Expiration Date is required';
      }

      if (privilegedFieldsRequired) {
        if (!values.certificationNumber) {
          errors.certificationNumber = 'Certification # is required';
        }

        if (!values.title) {
          errors.title = 'Title is required';
        }
      }

      if (values.certificationNumber && values.certificationNumber.length > 20) {
        errors.certificationNumber = 'Certification # must be 20 characters or fewer';
      } else if (values.certificationNumber && !/^[A-Za-z0-9-]*$/.test(values.certificationNumber)) {
        errors.certificationNumber = 'Certification # can only contain letters, numbers, and hyphens';
      }

      if (values.title && values.title.length > 250) {
        errors.title = 'Title must not exceed 250 characters';
      }

      if (!values.yearActivePracticeStarted) {
        errors.yearActivePracticeStarted = 'Year Active Practice Started is required';
      } else if (!/^\d{4}$/.test(values.yearActivePracticeStarted)) {
        errors.yearActivePracticeStarted = 'Year Active Practice Started must be a valid 4-digit year';
      } else if (Number(values.yearActivePracticeStarted) > new Date().getFullYear()) {
        errors.yearActivePracticeStarted = 'Year Active Practice Started cannot be in the future';
      }

      return errors;
    },
    onSubmit: () => {},
  });

  // Field components here don't support onBlur, so a field is marked "touched" (dirty)
  // the moment it's edited — this keeps errors from showing on untouched fields as soon
  // as any single field is changed (formik validates the whole form on every change).
  const setFieldValueTouched = <K extends keyof FormValues>(field: K, value: FormValues[K]) => {
    formik.setFieldValue(field, value);
    formik.setFieldTouched(field, true, false);
  };

  useEffect(() => {
    if (!open) return;

    if (isUpdate && certification?.fileName) {
      setUploadedFile({ serverId: certification.fileId ?? '', filename: certification.fileName, fileSize: 0 });
    } else {
      setUploadedFile(null);
    }

    if (isUpdate && certification) {
      formik.setValues({
        expirationDate: certification.expirationDate ?? '',
        certificationNumber: certification.certificationNumber ?? '',
        title: certification.title ?? '',
        yearActivePracticeStarted: certification.yearActivePracticeStarted ?? '',
      });
    } else {
      formik.resetForm();
    }
  }, [open, certification]);

  const uploadEndpoint = `${baseUrl}api/${buildEndpoint(ENDPOINTS.reviewerLicenseDocuments.base, METHODS.Upload)}`;

  const [postAdd] = useDataPoster(
    buildEndpoint(ENDPOINTS.reviewers.base, reviewerId, 'boardCertifications'),
    undefined,
    false,
    '3.0'
  );

  const [postUpdate] = useDataPoster(
    buildEndpoint(ENDPOINTS.reviewers.base, reviewerId, 'boardCertifications', certification?.id),
    undefined,
    true,
    '3.0'
  );

  const formatYear = (value: string) => value.replace(/\D/g, '').slice(0, 4);

  const handleSave = async () => {
    if (isUploading) return;

    setSaving(true);
    try {
      const fileId = uploadedFile?.serverId ?? '';
      const fileName = uploadedFile?.filename ?? '';
      const size = uploadedFile?.fileSize ?? 0;

      const { expirationDate, certificationNumber, title, yearActivePracticeStarted } = formik.values;

      let resp: any;
      if (isUpdate) {
        resp = await postUpdate({
          expirationDate: expirationDate || null,
          ...(hasManagePrivileged && {
            certificationNumber: certificationNumber.trim(),
            title: title.trim(),
          }),
          yearActivePracticeStarted: yearActivePracticeStarted.trim() || null,
          fileId,
          fileName,
          size,
        });
      } else {
        resp = await postAdd({
          expirationDate: expirationDate || null,
          certificationNumber: certificationNumber.trim(),
          title: title.trim(),
          yearActivePracticeStarted: yearActivePracticeStarted.trim() || null,
          fileId,
          fileName,
          size,
        });
      }

      if (resp?.status === 200 || resp?.status === 201) {
        const returned: BoardCertification = {
          id: resp.data?.id ?? certification?.id ?? '',
          expirationDate: resp.data?.expirationDate ?? expirationDate,
          isExpired: resp.data?.isExpired ?? false,
          certificationNumber: resp.data?.certificationNumber ?? certificationNumber,
          title: resp.data?.title ?? title,
          yearActivePracticeStarted: resp.data?.yearActivePracticeStarted ?? yearActivePracticeStarted,
          fileId: resp.data?.fileId ?? (fileId || undefined),
          fileName: fileName || undefined,
        };
        onSuccess(returned);
      } else if (resp?.response?.status === 400) {
        const data = resp.response?.data;
        if (data?.errors) {
          const mappedErrors: Record<string, string> = {};
          Object.entries(data.errors).forEach(([key, val]) => {
            const field = key.charAt(0).toLowerCase() + key.slice(1);
            mappedErrors[field] = (val as string[])[0];
          });
          formik.setErrors(mappedErrors);
          formik.setTouched(Object.keys(mappedErrors).reduce((acc, k) => ({ ...acc, [k]: true }), {}));
        }
        toast.error('Please validate the highlighted fields');
      } else if (resp?.response?.status === 403) {
        toast.error(resp.response?.data?.message ?? 'You do not have permission to perform this action.');
      } else {
        // 5xx or no response at all (network failure / blocked request).
        toast.error('Something went wrong');
      }
    } finally {
      setSaving(false);
    }
  };

  const drawerTitle = (
    <span className="sl-drawer-title">
      {isUpdate ? 'Update Board Certification' : 'Add Board Certification'}
    </span>
  );

  const footer = (
    <div className="sl-drawer-footer">
      <TertiaryButton title="Cancel" onClick={onClose} />
      <PrimaryButton
        title="Save"
        iconPosition="start"
        onClick={handleSave}
        disabled={saving || isUploading}
      >
        {saving && (
          <CircularProgress size={16} thickness={5} className="sl-drawer-save-spinner" />
        )}
      </PrimaryButton>
    </div>
  );

  return (
    <Drawer
      visible={open}
      onClose={onClose}
      title={drawerTitle}
      width={720}
      closable
      closeIcon={<CloseIcon />}
      footer={footer}
      destroyOnClose
      className="sl-drawer"
    >
      <div className="state-license-drawer-form">
        <div className="sl-drawer-fields-section">
          <p className="sl-drawer-description">
            Please provide the correct details on the reviewer's board certification.
          </p>

          <div className="sl-drawer-fields-grid">
          {/* Row 1: Expiration Date | Certification # */}
          <DateTimePickerInput
            label="Expiration Date"
            required
            value={formik.values.expirationDate}
            onChange={(val: string) => setFieldValueTouched('expirationDate', val)}
            error={formik.touched.expirationDate ? formik.errors.expirationDate : undefined}
            hint={isPastRawDate(formik.values.expirationDate) ? 'This expiration date is in the past' : undefined}
            dateOnly
          />
          <TextInput
            label="Certification #"
            required
            value={formik.values.certificationNumber}
            onChange={(e) => setFieldValueTouched('certificationNumber', e.target.value)}
            error={formik.touched.certificationNumber ? formik.errors.certificationNumber : undefined}
            maxLength={20}
            disabled={isUpdate && !hasManagePrivileged}
          />

          {/* Row 2: Title (full width) */}
          <div style={{ gridColumn: '1 / -1' }}>
            <TextInput
              label="Title"
              required
              value={formik.values.title}
              onChange={(e) => setFieldValueTouched('title', e.target.value)}
              error={formik.touched.title ? formik.errors.title : undefined}
              disabled={isUpdate && !hasManagePrivileged}
              maxLength={250}
            />
          </div>

          {/* Row 3: Year Active Practice Started (left col only) */}
          <TextInput
            label="Year Active Practice Started"
            required
            value={formik.values.yearActivePracticeStarted}
            onChange={(e) => setFieldValueTouched('yearActivePracticeStarted', formatYear(e.target.value))}
            error={formik.touched.yearActivePracticeStarted ? formik.errors.yearActivePracticeStarted : undefined}
          />
          </div>
        </div>

        <FileUploadBox
          uploadEndpoint={uploadEndpoint}
          initialFile={
            isUpdate && certification?.fileName
              ? { serverId: certification.fileId ?? '', filename: certification.fileName, fileSize: 0 }
              : undefined
          }
          label="Proof of Certification"
          note="This license will be uploaded to the reviewer's documents with the tag: Board Certification."
          onChange={(f) => setUploadedFile(f)}
          onUploadStateChange={(s) => setIsUploading(s === 'uploading')}
        />
      </div>
    </Drawer>
  );
};
